/**
 * Profile service for handling user profile operations
 */

import axiosClient, { ApiResponse } from "../../config/axiosClient";
import { ENDPOINTS } from "../../config/endpoints";
import { extractApiErrorMessage, showUploadErrorAlert } from "../../helpers/apiError";

export interface SellerAddressResponse {
  id: string;
  companyId: string;
  label?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
  isDefault: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface ProfileResponse {
  id: string;
  auth0Id: string;
  userId: string;
  email: string;
  auth0Email?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  whatsapp?: string;
  role: string;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
  bio?: string;
  website?: string;
  linkedinProfile?: string;
  profileImage?: string;
  businessType?: string;
  country?: string;
  sellerStatus?: string;
  company?: {
    id: string;
    name: string;
    email?: string;
    phone?: string;
    address?: string;
    city?: string;
    state?: string;
    country?: string;
    postalCode?: string;
    website?: string;
    description?: string;
    businessType?: string;
    companySize?: string;
    employeeCount?: number;
    industry?: string;
    foundedYear?: number;
    gstNumber?: string;
    panNumber?: string;
    taxId?: string;
    registrationNumber?: string;
    businessLicense?: string;
    factorySize?: string;
    productionCapacity?: string;
    qualityCertifications?: string;
    facebookUrl?: string;
    linkedinUrl?: string;
    twitterUrl?: string;
    logo?: string;
    acceptCashPayment?: boolean;
    acceptBankPayment?: boolean;
    bankName?: string;
    bankAccountName?: string;
    bankAccountNumber?: string;
    bankRoutingNumber?: string;
    sellerAddresses?: SellerAddressResponse[];
    approvalStatus?: string;
    approvedAt?: Date;
    isActive?: boolean;
    // Seller Business Profile (pepagora-style)
    legalBusinessName?: string;
    businessName?: string;
    legalStatus?: string;
    documentType?: string;
    documentNumber?: string;
    annualTurnover?: string;
    shippingAddressSameAsBusiness?: boolean;
    mainMarkets?: string[];
    shipmentModes?: string[];
    acceptedPaymentCurrency?: string;
    paymentTerms?: string;
    paymentMethods?: string[];
    iecNumber?: string;
    iecDocumentUrl?: string;
    exportPercentage?: number;
    languagesSpoken?: string[];
    nearestPort?: string;
    averageLoadTimeValue?: string;
    averageLoadTimeUnit?: string;
    minOrderValueCurrency?: string;
    minOrderValueAmount?: string;
    acceptedDeliveryTerms?: string;
    contractManufacturing?: string;
    totalFactorySize?: string;
    infrastructureOverview?: string;
    productionLinesCount?: number;
    annualOutputValue?: string;
    qcStaffCount?: number;
    rndStaffCount?: number;
    productionFacilities?: string;
    warehouseStorageArea?: string;
    companyVideoUrl?: string;
    youtubeUrl?: string;
    instagramUrl?: string;
    businessProfileExtended?: Record<string, unknown>;
    selectedMembershipPlanId?: string;
    industryTypes?: string[];
  };
  editableFields?: string[];
  nonEditableFields?: string[];
}

export interface SellerAddressItemRequest {
  id?: string;
  label?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
  isDefault?: boolean;
}

export interface UpdateProfileRequest {
  firstName?: string;
  lastName?: string;
  phone?: string;
  whatsapp?: string;
  bio?: string;
  website?: string;
  linkedinProfile?: string;
  profileImage?: string;
  businessType?: string;
  country?: string;
  companyName?: string;
  companyEmail?: string;
  companyPhone?: string;
  companyAddress?: string;
  companyCity?: string;
  companyState?: string;
  companyCountry?: string;
  companyPostalCode?: string;
  companyWebsite?: string;
  companyDescription?: string;
  companyBusinessType?: string;
  companySize?: string;
  employeeCount?: number;
  industry?: string;
  foundedYear?: number;
  gstNumber?: string;
  panNumber?: string;
  gstDocumentUrl?: string;
  panDocumentUrl?: string;
  taxId?: string;
  registrationNumber?: string;
  businessLicense?: string;
  factorySize?: string;
  productionCapacity?: string;
  qualityCertifications?: string;
  facebookUrl?: string;
  linkedinUrl?: string;
  twitterUrl?: string;
  companyLogo?: string;
  acceptCashPayment?: boolean;
  acceptBankPayment?: boolean;
  bankName?: string;
  bankAccountName?: string;
  bankAccountNumber?: string;
  bankRoutingNumber?: string;
  sellerAddresses?: SellerAddressItemRequest[];
  // Seller Business Profile (pepagora-style)
  legalBusinessName?: string;
  businessName?: string;
  legalStatus?: string;
  documentType?: string;
  documentNumber?: string;
  annualTurnover?: string;
  shippingAddressSameAsBusiness?: boolean;
  mainMarkets?: string[];
  shipmentModes?: string[];
  acceptedPaymentCurrency?: string;
  paymentTerms?: string;
  paymentMethods?: string[];
  iecNumber?: string;
  iecDocumentUrl?: string;
  exportPercentage?: number;
  languagesSpoken?: string[];
  nearestPort?: string;
  averageLoadTimeValue?: string;
  averageLoadTimeUnit?: string;
  minOrderValueCurrency?: string;
  minOrderValueAmount?: string;
  acceptedDeliveryTerms?: string;
  contractManufacturing?: string;
  totalFactorySize?: string;
  infrastructureOverview?: string;
  productionLinesCount?: number;
  annualOutputValue?: string;
  qcStaffCount?: number;
  rndStaffCount?: number;
  productionFacilities?: string;
  warehouseStorageArea?: string;
  companyVideoUrl?: string;
  youtubeUrl?: string;
  instagramUrl?: string;
  businessProfileExtended?: Record<string, unknown>;
  selectedMembershipPlanId?: string;
  industryTypes?: string[];
}

class ProfileService {
  /**
   * Get current user profile
   */
  async getProfile(): Promise<ApiResponse<ProfileResponse>> {
    const response = await axiosClient.get<ApiResponse<ProfileResponse>>(
      `${ENDPOINTS.SELLER.PROFILE}`
    );
    return response.data;
  }

  /**
   * Update current user profile
   */
  async updateProfile(
    profileData: UpdateProfileRequest
  ): Promise<ApiResponse<ProfileResponse>> {
    const response = await axiosClient.patch<ApiResponse<ProfileResponse>>(
      `${ENDPOINTS.SELLER.UPDATE_PROFILE}`,
      profileData
    );
    return response.data;
  }

  /**
   * Upload a file (image/document) to DigitalOcean Spaces via the profile upload endpoint.
   * Returns the public URL of the uploaded file.
   */
  async uploadFile(file: File): Promise<string> {
    const formData = new FormData();
    formData.append("file", file);
    try {
      const response = await axiosClient.post<{ success: boolean; url: string }>(
        ENDPOINTS.SELLER.UPLOAD_FILE,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 60000,
        },
      );
      const data = response.data as { url?: string; data?: { url?: string } };
      const url = data?.url || data?.data?.url || "";
      if (!url) {
        throw new Error("Upload failed. No file URL returned.");
      }
      return url;
    } catch (error: unknown) {
      const reason = extractApiErrorMessage(
        error,
        "Upload failed. Please try again."
      );
      showUploadErrorAlert(reason);
      throw new Error(reason);
    }
  }
}

// Export singleton instance
export const profileService = new ProfileService();
export default profileService;

